/** Custom jewelry (/custom-jewelry) — storefront labels and promo copy. */
import {
  BESPOKE_JEWELRY_TYPE_DEFAULTS,
  BESPOKE_OCCASION_DEFAULTS,
} from './bespokeFormOptionDefaults';

export const CUSTOM_JEWELRY_DEFAULTS = {
  enabled: true,
  show_on_shop: true,
  show_in_nav: true,
  show_in_main_nav: true,
  show_in_footer: true,
  show_on_gift_finder: true,
  design_gallery_enabled: true,
  shop_button_label: 'Custom jewelry design',
  nav_label: 'Custom jewelry',
  main_nav_label: 'Custom',
  card_eyebrow: 'Made to order',
  card_title: 'Design jewelry for your celebration',
  card_lead: 'Share your outfit photo — we match colours for haldi, mehendi, sangeet, or wedding.',
  card_cta: 'Start your design',
  hero_eyebrow: 'Bespoke studio',
  hero_title: 'Design your own set,',
  hero_lead:
    'Browse pieces we have crafted for real brides and festive looks — then share your outfit photo and we will colour-match jardosi jewelry for haldi, mehendi, sangeet, or wedding.',
  hero_cta_primary: 'Create your design now',
  hero_cta_gallery: 'Explore our designs',
  design_gallery_badge_label: 'handcrafted designs',
  hero_whatsapp_lead: 'Not ready to upload photos yet? Message us your occasion first — we will guide you.',
  hero_whatsapp_cta: 'Chat on WhatsApp',
  hero_whatsapp_message:
    'Hi, I am interested in custom jewelry design for my celebration. Can you help me get started?',
  hero_image: '',
  hero_images: [],
  seo_title: 'Custom jewelry design | Saloni Designs',
  seo_description:
    'Share your outfit photo and occasion — we design handcrafted jardosi and festive jewelry made to order.',
  gift_finder_promo_title: 'Need something bespoke?',
  gift_finder_promo_lead:
    'We design jewelry to match your outfit for haldi, mehendi, sangeet, and wedding — upload a dress photo and get a quote.',
  gift_finder_promo_cta: 'Request custom design',
  trust_items: [
    { icon: '🎨', title: 'Colour matched', text: 'We design around your outfit photo' },
    { icon: '💬', title: 'Personal consult', text: 'Ideas & quote by WhatsApp or email' },
    { icon: '✋', title: 'Handcrafted', text: 'Jardosi & festive work in our studio' },
  ],
  breadcrumb_shop_label: 'Shop',
  breadcrumb_current_label: '',
  how_it_works_title: 'How it works',
  how_it_works_steps: [
    { text: 'Choose your occasion — haldi, mehendi, sangeet, or wedding.' },
    { text: 'Select jewellery & pieces within your look (necklace, earrings, maang tikka, and more).' },
    { text: 'Share your outfit photo — we reply with ideas and a quote.' },
  ],
  pendants_note_title: 'Ready-made with photo lockets?',
  pendants_note_before_link: 'Browse',
  pendants_note_link_label: 'pendants',
  pendants_note_link_href: '/shop/pendants',
  pendants_note_after_link:
    'and customize on the product page — chain length, photo upload, and more.',
  success_title: 'Request received',
  success_ref_prefix: 'Reference',
  success_fine_print:
    'We will reply on WhatsApp or email with design ideas and a quote. No payment is needed now.',
  success_view_requests_button: 'View my custom requests',
  success_sign_in_button: 'Sign in to track requests',
  success_submit_another_button: 'Submit another request',
  submit_success_message: 'Thank you — we will reach out with design ideas and a quote.',
  form_modal_title: 'Custom jewelry design',
  form_modal_subtitle: 'Bespoke studio',
  form_modal_lead:
    'Tell us about your celebration — we colour-match handcrafted jardosi jewelry to your outfit. No payment until you approve the design.',
  form_section_details: 'Your details',
  form_section_event: 'Your event',
  form_section_photos: 'Photos & inspiration',
  signed_in_note:
    'Signed in as {name} — your details are filled in below. Edit anything that should be different for this request.',
  form_label_name: 'Your name',
  form_label_phone: 'Phone / WhatsApp',
  form_label_email: 'Email',
  form_label_occasion: 'Occasion *',
  form_label_occasion_hint: 'Select all that apply',
  form_label_jewelry_type: 'Jewelry type *',
  form_label_jewelry_type_hint: 'Select all that apply',
  form_label_event_date: 'Event date',
  form_label_event_date_optional: '(optional)',
  form_section_photos_intro:
    'Photos help us colour-match your jewelry perfectly. Clear, well-lit shots of your outfit work best.',
  form_upload_dress_badge: 'Required',
  form_upload_ref_badge: 'Optional',
  form_upload_dress_tips: ['Full outfit or lehenga/saree', 'Close-up of fabric colour', 'Natural daylight if possible'],
  form_upload_ref_tips: ['Pinterest or Instagram saves', 'Previous jewelry you loved', 'Any style notes in screenshots'],
  form_upload_dress_label: 'Outfit / dress photos',
  form_upload_dress_hint:
    'Upload your saree, lehenga, or outfit — choose multiple photos at once, or add angles and close-ups of the fabric colour.',
  form_upload_ref_label: 'Reference / inspiration',
  form_upload_ref_hint:
    'Pinterest screenshots, previous jewelry, or looks you love — add as many reference photos as you like (optional).',
  form_label_notes: 'Design notes',
  form_notes_placeholder: 'Colours, jardosi style, budget range, pieces needed…',
  submit_button: 'Request custom design',
  submit_busy_label: 'Sending request…',
  submit_fine_print: "No payment now — we'll reply with ideas and a quote first.",
  occasions: BESPOKE_OCCASION_DEFAULTS.map((item) => ({ ...item })),
  jewelry_types: BESPOKE_JEWELRY_TYPE_DEFAULTS.map((item) => ({ ...item })),
  design_gallery_eyebrow: 'From our studio',
  design_gallery_title: 'Designs we have crafted',
  design_gallery_lead:
    'Real bespoke pieces made for our customers — browse for inspiration, then share your outfit photo to start your own design.',
  design_gallery_use_cta: 'Love this style',
  design_gallery_form_hint: 'We will note this look when you submit your request.',
  design_gallery_view_all: 'Browse full gallery',
  design_gallery_load_more: 'Load more designs',
  design_gallery_loading_more: 'Loading…',
  design_gallery_breadcrumb: 'Design gallery',
  design_gallery_page_title: 'Our custom jewelry designs',
  design_gallery_page_lead:
    'Explore handcrafted pieces we have made for real celebrations — haldi, mehendi, sangeet, and wedding. Find a style you love, then tell us about your outfit.',
  design_gallery_seo_title: 'Custom jewelry design gallery | Saloni Designs',
  design_gallery_seo_description:
    'Browse 100+ bespoke jewelry designs crafted in our studio — get inspired and start your own custom piece.',
  design_gallery_cta_lead: 'Ready to design something made just for you?',
  design_gallery_cta_button: 'Start your custom design',
  design_gallery: [],
};
